import React, {useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
  Label,
  Modal,
  IconName,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {CONTENT_STUDIO_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {FEATURE_ASSETS, FEATURE_ID} from '@/views/content-studio/constants';
import type {FeatureId} from '@/views/content-studio/constants';
import {contentStudioActions} from '@/stores/contentStudioStore';
import {media} from '@/styles/mediaQueries';
import styles from './DoAndDontModal.module.scss';

interface DoAndDontModalProps {
  feature: FeatureId;
}

interface ModalSettings {
  titleLocaleKey: string;
  iconName: IconName;
  featureImageUrl: string;
  doLocaleKeys: string[];
  dontLocaleKeys: string[];
}

const MODAL_SETTINGS = {
  [FEATURE_ID.RESIZE]: {
    titleLocaleKey: 'Reformatting to any platform',
    iconName: 'Actions-Expand',
    featureImageUrl: FEATURE_ASSETS.RESIZE.IMAGE,
    doLocaleKeys: [
      'Center the subject with room around them for clean cropping',
      'Use simple, steady backdrops like plain walls or soft backgrounds',
      'Keep key visuals within the center 80% of frame and avoid overlays',
    ],
    dontLocaleKeys: [
      'Use wide shots with people far apart',
      'Place text or logos near the edge',
      'Upload shaky, dark, or blurry footage',
    ],
  },
  [FEATURE_ID.DUB]: {
    titleLocaleKey: 'Dubbing to multiple languages',
    iconName: 'Navigation-Messages',
    featureImageUrl: FEATURE_ASSETS.DUB.IMAGE,
    doLocaleKeys: [
      'Use well-lit clips with the speaker facing the camera',
      'Start with clean audio-no background noise or echo',
      'Stick to one visible speaker or clear cuts between speakers',
    ],
    dontLocaleKeys: [
      'Block the speakers mouth with hands, mics, or captions',
      'Use clips with fast edits, singing, or overlapping voices',
      'Upload phone or echo-heavy audio',
    ],
  },
  [FEATURE_ID.REPHRASE]: {
    titleLocaleKey: 'Rescript',
    iconName: 'Features-Rename',
    featureImageUrl: FEATURE_ASSETS.REPHRASE.IMAGE,
    doLocaleKeys: [
      'Swap short phrases of similar length for smooth syncing',
      'Edit directly in the transcript panel and preview before saving',
      'Choose clips with natural pauses and complete sentence endings',
    ],
    dontLocaleKeys: [
      "Rewrite large sections-longer lines won't sync with lips",
      'Overlap dialogue with music, crowd noise, or background voices',
      'Use words and/or sentences that are much longer the original',
    ],
  },
} as {[key: FeatureId]: ModalSettings};

const StyledModal = styled(Modal)`
  background: rgba(0, 0, 0, 0.75);
  padding: 0;
`;

const Container = styled(Box)`
  display: flex;
  gap: 36px;
  flex: 1;

  ${media.mobile} {
    max-width: 100%;
    flex-direction: column;
  }
`;

const ImageContainer = styled(Box)`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const Image = styled('img')`
  width: 575px;
  height: 575px;
  border-radius: 16px;
  object-fit: cover;

  ${media.mobile} {
    width: unset;
    height: unset;
    max-width: 100%;
    max-height: 100%;
  }
`;

const ContentContainer = styled(Box)`
  box-sizing: border-box;
  display: flex;
  width: 575px;
  height: 575px;
  padding-top: 4px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;

  ${media.mobile} {
    width: 100%;
    height: unset;
  }
`;

const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 48px;
`;

const HeadlineContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ItemListsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
`;

const ListContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const ListItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

function ItemsList({
  title,
  items,
  itemIcon,
}: {
  title: string;
  items: string[];
  itemIcon: React.ReactNode;
}) {
  return (
    <ListContainer>
      <Headline size="md" color={designSystemToken('semantic.fg.neutral')}>
        {title}
      </Headline>
      {items.map((item) => (
        <ListItem key={item}>
          {itemIcon}
          <Body size="xl" color={designSystemToken('semantic.fg.secondary')}>
            {item}
          </Body>
        </ListItem>
      ))}
    </ListContainer>
  );
}

function DoAndDontModal(props: DoAndDontModalProps) {
  const {feature} = props;

  const {uiInteraction} = useUiInteractionEvent();
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const [interactionId, setInteractionId] = useState(uuid());

  useEffect(() => {
    if (feature) {
      viewPresented({
        view_name: CONTENT_STUDIO_VIEW_NAMES.DO_AND_DONT,
        view_type: 'modal',
        view_location: feature,
        presentation_id:
          AnalyticsService.getActiveScreenPresented().screen_presentation_id,
        interaction_id: interactionId,
        source: 'do_and_dont_modal',
        view_details: `do_and_dont_feature: ${feature}`,
      });
    }

    return () => {
      if (feature) {
        viewDismissed({
          presentation_id:
            AnalyticsService.getActiveScreenPresented().screen_presentation_id,
          interaction_id: interactionId,
          reason: 'dismissed',
          view_details: `do_and_dont_feature: ${feature}`,
        });
        setInteractionId(uuid());
      }
    };
  }, [feature]);

  if (!feature) {
    return null;
  }

  const settings = MODAL_SETTINGS[feature];

  if (!settings) {
    return null;
  }

  return (
    <StyledModal open contentClassName={styles.modal}>
      <Container>
        <ImageContainer>
          <Image
            src={settings.featureImageUrl}
            alt={translate(settings.titleLocaleKey)}
          />
        </ImageContainer>
        <ContentContainer>
          <Content>
            <HeadlineContainer>
              <Headline
                size="xl"
                color={designSystemToken('semantic.fg.neutral')}
              >
                {translate(settings.titleLocaleKey)}
              </Headline>
              <Icon
                size="large"
                appearance="neutral"
                name={settings.iconName}
                color={designSystemToken('semantic.fg.neutral')}
                style={{width: '36px', height: '36px'}}
              />
            </HeadlineContainer>
            <ItemListsContainer>
              <ItemsList
                title={`${translate('Do')}:`}
                items={settings.doLocaleKeys.map((item) => translate(item))}
                itemIcon={
                  <Icon
                    size="small"
                    appearance="neutral"
                    name="Actions-Patch-Accept-Fill"
                    color={designSystemToken('semantic.bg.success')}
                  />
                }
              />
              <ItemsList
                title={`${translate("Don't")}:`}
                items={settings.dontLocaleKeys.map((item) => translate(item))}
                itemIcon={
                  <Icon
                    size="small"
                    appearance="neutral"
                    name="Actions-Patch-Cancel-Fill"
                    color={designSystemToken('semantic.bg.danger')}
                  />
                }
              />
            </ItemListsContainer>
          </Content>
          <Button
            appearance="neutral"
            mode="filled"
            size="large"
            sx={{width: '100%'}}
            onClick={() => {
              uiInteraction({
                item_name: 'accept_do_and_dont_button',
                interaction_location: feature,
                interaction_id: interactionId,
              });
              contentStudioActions.setDoAndDontModalFeature('');
              contentStudioActions.setDoAndDontModalFeatureSeen(feature);

              // uncomment to enable feature seen tracking - should be moved to a "Don't show again" button (pending design)
              // setPersistedDoAndDontModalFeatureSeen(feature);
            }}
          >
            <Label
              size="lg"
              color={designSystemToken('semantic.fg.neutral-secondary')}
            >
              {translate('Got it')}
            </Label>
          </Button>
        </ContentContainer>
      </Container>
    </StyledModal>
  );
}

export default DoAndDontModal;
